import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Checkbox } from "../components/ui/Checkbox";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";

/**
 * Beyond Sure — Phone Sign-In (number-entry) screen.
 *
 * Sister screen to SignInScreen.tsx — same AuthShell, same brand language,
 * same T&C gate, but accepts an Indian mobile number instead of an email.
 * Submits to /api/auth/phone/send-otp; on success the parent advances to
 * PhoneOtpScreen for the 6-digit verify.
 *
 * Gating: this entire path is hidden from production until
 * PHONE_LOGIN_ENABLED flips in lib/featureFlags.ts. The reason it lives in
 * the repo dormant rather than as a future PR: the founder wanted the flow
 * pre-built so SMS provider activation is a one-line constant flip instead
 * of a "now let's design a new screen" sprint.
 *
 * Phone format expectations (matches server's normalize_phone at
 * server.py:234):
 *   - 10 digits → assumed +91 (India)
 *   - 11 digits starting with "0" → strip 0, assumed +91
 *   - 12 digits starting with "91" → already country-coded
 *   - +91XXXXXXXXXX → already country-coded
 * Anything else is rejected client-side before the request.
 */

type Phase = "form" | "sent";

function ArrowRightGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Strip every non-digit, then return what the server's normalize_phone will
 *  accept. Returns "" when the input can't possibly be an Indian mobile. */
function toIndianMobile(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10 && /^[6-9]/.test(digits)) return digits;
  if (digits.length === 11 && digits.startsWith("0") && /^0[6-9]/.test(digits))
    return digits.slice(1);
  if (digits.length === 12 && digits.startsWith("91") && /^91[6-9]/.test(digits))
    return digits.slice(2);
  if (digits.length === 13 && digits.startsWith("091")) return digits.slice(3);
  return "";
}

function isValidIndianMobile(raw: string): boolean {
  return toIndianMobile(raw).length === 10;
}

function phoneFieldError(raw: string): string | null {
  if (!raw.trim()) return "Please enter your mobile number.";
  if (!isValidIndianMobile(raw))
    return "Enter a 10-digit Indian mobile number (starting 6, 7, 8, or 9).";
  return null;
}

interface PhoneSignInScreenProps {
  /** Called when an OTP has been requested. Parent advances to PhoneOtpScreen
   *  with the normalized 10-digit number so the verify call can echo it back. */
  onSent?: (normalizedPhone: string) => void;
  initialPhone?: string;
  /** Called when the user wants to switch back to the email flow. */
  onBackToEmail?: () => void;
}

export function PhoneSignInScreen({
  onSent,
  initialPhone = "",
  onBackToEmail,
}: PhoneSignInScreenProps) {
  const [phone, setPhone] = useState(initialPhone);
  const [touched, setTouched] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>("form");
  const [shake, setShake] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const error = useMemo(
    () => (touched ? phoneFieldError(phone) : null),
    [phone, touched],
  );
  const canSubmit = isValidIndianMobile(phone) && agreed && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!canSubmit) {
      setShake((n) => n + 1);
      return;
    }
    setLoading(true);
    const normalized = toIndianMobile(phone);
    // POST /api/auth/phone/send-otp — server applies its own normalize_phone
    // and rate-limits (3/number/10min, 5/IP/10min). On non-2xx we surface
    // the message in a red banner; on success the parent advances.
    try {
      const r = await fetch("/api/auth/phone/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: normalized }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg =
          (data && (data.message || data.error)) ||
          "Could not send code. Please try again.";
        setSubmitError(msg);
        setShake((n) => n + 1);
        setLoading(false);
        return;
      }
      setLoading(false);
      if (onSent) {
        onSent(normalized);
      } else {
        setPhase("sent");
      }
    } catch {
      setSubmitError(
        "Network error. Please check your connection and try again.",
      );
      setShake((n) => n + 1);
      setLoading(false);
    }
  }

  return (
    <AuthShell
      eyebrow="Sign in · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      showcase={<PhoneShowcase />}
    >
      <AnimatePresence mode="wait" initial={false}>
        {phase === "form" ? (
          <motion.form
            key="phone-form"
            onSubmit={handleSubmit}
            noValidate
            animate={{ x: shake ? [-6, 6, -4, 4, 0] : 0 }}
            transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
            initial={{ opacity: 0, y: 6 }}
            exit={{ opacity: 0, y: -6 }}
            className="bsure-ui flex flex-col gap-5"
          >
            {submitError ? (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
              >
                {submitError}
              </div>
            ) : null}

            <Input
              label="Mobile number"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              autoFocus
              placeholder="98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setTouched(true)}
              error={error}
              helper="Indian mobile only. We'll text a 6-digit code via SMS."
            />

            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              label={
                <>
                  I agree to the{" "}
                  <a
                    href="#terms"
                    className="text-[var(--color-brand)] underline underline-offset-2 decoration-[var(--color-brand)] hover:text-[var(--color-brand-deep)] transition-colors"
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="#privacy"
                    className="text-[var(--color-brand)] underline underline-offset-2 decoration-[var(--color-brand)] hover:text-[var(--color-brand-deep)] transition-colors"
                  >
                    Privacy Policy
                  </a>
                  . I understand BeyondSure provides advisory information only
                  and is not a licensed insurance broker.
                </>
              }
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={!isValidIndianMobile(phone) || !agreed}
              trailing={<ArrowRightGlyph />}
            >
              Send SMS code
            </Button>

            {onBackToEmail ? (
              <button
                type="button"
                onClick={onBackToEmail}
                className="bsure-ui mt-1 self-start text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors"
              >
                ← Use email instead
              </button>
            ) : null}

            <p className="mt-1 text-[12.5px] leading-[1.5] text-[var(--color-ink-40)]">
              New here? An account is created automatically the first time you
              sign in. Standard SMS charges may apply on your carrier.
            </p>
          </motion.form>
        ) : (
          <motion.div
            key="phone-sent"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
            className="bsure-ui flex flex-col gap-5"
            role="status"
            aria-live="polite"
          >
            <div className="bsure-display text-[1.5rem] leading-[1.1]">
              Code on its way to{" "}
              <span className="text-[var(--color-brand-deep)]">+91 {phone}</span>
            </div>
            <p className="text-[14px] text-[var(--color-ink-60)] leading-[1.5]">
              We sent a 6-digit code over SMS. It usually arrives in under a
              minute.
            </p>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                setPhase("form");
                setLoading(false);
              }}
            >
              Use a different number
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthShell>
  );
}

export default PhoneSignInScreen;
